/** Pure helpers for invite links, room codes and invitation status. */

export const JOIN_PATH_PREFIX = '/join/';
export const PUBLIC_WEB_ORIGIN = 'https://app.bhabi.ca';

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isRoomId = (value: string): boolean => UUID_RE.test(value);

/**
 * Accepts whatever a friend pasted — a full invite URL, a `/join/<id>` path,
 * or the bare room id — and returns the room id, or null when it cannot be
 * a valid table.
 */
export const parseRoomInput = (raw: string): string | null => {
  const input = (raw || '').trim();
  if (!input) return null;

  const joinIdx = input.indexOf(JOIN_PATH_PREFIX);
  let candidate = input;
  if (joinIdx >= 0) {
    candidate = input.slice(joinIdx + JOIN_PATH_PREFIX.length);
  }
  candidate = candidate.split(/[?#/\s]/)[0];

  return isRoomId(candidate) ? candidate.toLowerCase() : null;
};

export const buildInvitePath = (roomID: string) =>
  `${JOIN_PATH_PREFIX}${encodeURIComponent(roomID)}`;

export const buildInviteUrl = (origin: string, roomID: string) =>
  `${origin.replace(/\/+$/, '')}${buildInvitePath(roomID)}`;

/** Short, human friendly table code shown next to the link (display only). */
export const shortTableCode = (roomID: string) =>
  roomID.replace(/-/g, '').slice(0, 6).toUpperCase();

export type InvitationStatus = 'waiting' | 'playing' | 'concluded';

export interface Invitation {
  roomID: string;
  numberOfPlayers: number;
  joinedPlayers: number;
  status: InvitationStatus;
}

export type InvitationProblem = 'full' | 'in-progress' | 'finished' | null;

/**
 * Why a visitor cannot take a seat, or null when they can. Members of the
 * table can always rejoin a game that is in progress.
 */
export const invitationProblem = (
  inv: Invitation,
  { isMember }: { isMember: boolean }
): InvitationProblem => {
  if (isMember) return null;
  if (inv.status === 'concluded') return 'finished';
  if (inv.status === 'playing') return 'in-progress';
  if (inv.joinedPlayers >= inv.numberOfPlayers) return 'full';
  return null;
};

export const PROBLEM_COPY: Record<
  Exclude<InvitationProblem, null>,
  { title: string; body: string }
> = {
  full: {
    title: 'This table is full',
    body: 'Every seat has been taken. Ask the host to start a new table and send you the fresh link.',
  },
  'in-progress': {
    title: 'The game has already started',
    body: 'Cards are already dealt at this table. If you were seated here before, try rejoining; otherwise ask for a new link.',
  },
  finished: {
    title: 'This game has finished',
    body: 'The table has closed. Start your own table and invite everyone back.',
  },
};

/**
 * Map an OS-delivered URL (universal link `https://app.bhabi.ca/join/<id>` or
 * custom scheme `bhabi://join/<id>`) to an in-app route, or null.
 */
export const deepLinkToPath = (
  url: string | null | undefined
): string | null => {
  if (!url) return null;
  const roomID = url.includes(JOIN_PATH_PREFIX) ? parseRoomInput(url) : null;
  return roomID ? buildInvitePath(roomID) : null;
};
